// Status → colour for the Queue Health (8B) indicator.
//
// Two vocabularies have shipped. The Express endpoint derived one of
// healthy / processing / stalled / failed, plus unknown on error
// (controllers/aim.controller.js:624-631). fn_get_aim_queue_health returns
// `degraded` instead, which was in neither the old set nor the card's colour map.
//
// The consequence was quiet: an unmapped status fell through to the map's own
// `unknown` grey, so a card reporting "Degraded — 3 failed" showed the same
// neutral dot as "we have no idea". The one visual signal the card exists for was
// cancelled out, with no error to notice.
//
// So both vocabularies are supported, and an unrecognised status is reported as
// such rather than silently borrowing the `unknown` styling.

const KNOWN = {
  healthy: '#10B981',     // emerald — jobs flowing
  processing: '#F59E0B',  // amber — work in flight
  degraded: '#F97316',    // orange — failures present, queue not blocked
  stalled: '#F97316',     // orange — jobs stuck
  failed: '#EF4444',      // red — the queue itself is failing
  unknown: '#9CA3AF',     // grey — the backend explicitly could not tell
  // UI-only pseudo-status, not from any backend: the selected run completed but
  // logged errored responses. Lives here so the header pill and the System tab
  // card draw from one palette.
  errors: '#F59E0B',      // amber
};

// A queue in one of these states is a system-level problem, and outranks a
// per-run warning in the header pill. `degraded` belongs here and was the value
// the old inline checks missed, which cost it that precedence.
const UNHEALTHY = new Set(['degraded', 'stalled', 'failed']);

const FALLBACK = '#9CA3AF';

/**
 * Resolve the indicator colour for a queue status.
 *
 * `recognised` distinguishes a status the map knows about — including the
 * backend's explicit `unknown` — from one it has never seen. Callers should make
 * the unrecognised case visibly different so the next vocabulary change surfaces
 * instead of hiding behind a plausible-looking grey dot.
 *
 * @param {string|null|undefined} status the payload's `status`
 * @returns {{hex: string, recognised: boolean}}
 */
export function queueHealthColor(status) {
  const key = typeof status === 'string' ? status.trim().toLowerCase() : '';
  return Object.prototype.hasOwnProperty.call(KNOWN, key)
    ? { hex: KNOWN[key], recognised: true }
    : { hex: FALLBACK, recognised: false };
}

/**
 * Whether the queue itself is in trouble, as opposed to a single run having
 * logged errors.
 *
 * The header pill shows one of two things: a broken queue, or a completed run
 * with errored responses. The queue outranks the run — but the check was written
 * as `status === 'failed' || status === 'stalled'`, so the RPC's `degraded` did
 * not qualify and a degraded queue silently lost precedence to the run warning.
 *
 * @param {string|null|undefined} status the payload's `status`
 * @returns {boolean}
 */
export function isQueueUnhealthy(status) {
  return UNHEALTHY.has(typeof status === 'string' ? status.trim().toLowerCase() : '');
}

export const QUEUE_HEALTH_STATUSES = Object.keys(KNOWN);
